import React from 'react';
import { Shield, ArrowRight, ExternalLink, Star, Users, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import * as amplitude from '@amplitude/analytics-browser';

interface HeroSectionProps {
  image: string;
  title: string;
  content: string;
}

export function HeroSection({ image, title, content }: HeroSectionProps) {
  return (
    <div className="relative min-h-[520px] sm:min-h-[580px] flex items-end sm:items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={image}
          alt="Globe PVT Insurance"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-gray-900/30 sm:bg-gradient-to-r sm:from-gray-900/95 sm:via-gray-900/70 sm:to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-5">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-xs sm:text-sm font-medium text-white">Recommande par pvtistes.net depuis 2005</span>
          </div>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
            {title}
          </h1>

          <div
            className="text-[15px] sm:text-base text-white/80 leading-relaxed mb-6 max-w-xl [&_p]:mb-0"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          <div className="flex items-center gap-4 sm:gap-6 mb-8 text-white/70 text-xs sm:text-sm">
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
              <span>4.8/5 de satisfaction</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span>100 000+ assures</span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>Assistance 24h/24</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://www.acs-ami.com/fr/part/pvtistes/globe-pvt/?part=pvtistes&utm_source=pvtistes-pwa&utm_medium=aff-link&utm_campaign=gp-pvt-pwa"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => amplitude.track("Insurance CTA Clicked", { location: "hero" })}
              className="group flex items-center justify-center gap-2 px-6 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>Obtenir un devis gratuit</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="https://clems.acs-ami.com/fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium rounded-xl transition-all border border-white/20 hover:border-white/30"
            >
              <span>Demande de remboursement</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
